'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/app/homepage/components/Footer';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/homepage" className="flex items-center space-x-2">
              <svg
                width="32"
                height="32"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-primary"
              >
                <path
                  d="M20 4L4 12V28L20 36L36 28V12L20 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 20L28 16M20 20L12 16M20 20V28"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xl font-heading font-bold">Signaly</span>
            </Link>
            <Link
              href="/homepage"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Icon name="HomeIcon" size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Icon name="ShieldCheckIcon" size={20} className="mr-2" />
              <span className="text-sm font-medium">Your Privacy Matters</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Last Updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-xl p-8 border border-border space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">1. Introduction</h2>
            <p className="text-muted-foreground">
              Signaly ("we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">2. Information We Collect</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Personal Information:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Name, email address, and phone number (when you create an account)</li>
                  <li>Profile information you choose to provide</li>
                  <li>OAuth credentials (Google, Instagram, TikTok) for authentication</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Usage Data:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>App interactions, feature usage, and navigation patterns</li>
                  <li>Paper trading activity and portfolio performance</li>
                  <li>Signal viewing and engagement history</li>
                  <li>Device information (type, OS version, unique identifiers)</li>
                  <li>IP address and approximate location</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Financial Data:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Paper trading transactions (simulated only, no real money)</li>
                  <li>Subscription and payment information (processed by third-party payment providers)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Communications:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Messages you send through the App</li>
                  <li>Customer support interactions</li>
                  <li>Survey responses and feedback</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-3">We use your information to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Provide, maintain, and improve the App's functionality</li>
              <li>Create and manage your account</li>
              <li>Process subscriptions and payments</li>
              <li>Generate personalized trading signals and recommendations</li>
              <li>Send push notifications about new signals and market updates</li>
              <li>Communicate with you about your account and our services</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Detect and prevent fraud, abuse, and security incidents</li>
              <li>Comply with legal obligations</li>
              <li>Enforce our Terms of Service</li>
            </ul>
          </section>

          {/* How We Share Your Information */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">4. How We Share Your Information</h2>
            <p className="text-muted-foreground mb-3">We may share your information with:</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Service Providers:</h3>
                <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                  <li>Cloud hosting (Convex for backend storage)</li>
                  <li>Authentication services (Google, Instagram, TikTok OAuth)</li>
                  <li>Payment processors (for subscription billing)</li>
                  <li>Analytics providers</li>
                  <li>Customer support tools</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Legal Requirements:</h3>
                <p className="text-muted-foreground">
                  We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Business Transfers:</h3>
                <p className="text-muted-foreground">
                  If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Aggregated Data:</h3>
                <p className="text-muted-foreground">
                  We may share anonymized, aggregated data that cannot identify you personally for research, analytics, or marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">5. Third-Party Services</h2>
            <p className="text-muted-foreground mb-3">The App integrates with third-party services:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Google: OAuth authentication</li>
              <li>Instagram: OAuth authentication and content sharing</li>
              <li>TikTok: OAuth authentication and content sharing</li>
              <li>Twelve Data: Market data and pricing</li>
              <li>Binance: Cryptocurrency data</li>
              <li>Convex: Backend database and API</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              These services have their own privacy policies. We encourage you to review them.
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">6. Data Security</h2>
            <p className="text-muted-foreground mb-3">We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Secure authentication with OAuth 2.0</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and monitoring</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your personal information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">7. Data Retention</h2>
            <p className="text-muted-foreground mb-3">We retain your personal information for as long as necessary to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Provide the App's services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it by law.
            </p>
          </section>

          {/* Your Privacy Rights */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">8. Your Privacy Rights</h2>
            <p className="text-muted-foreground mb-3">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise your rights, contact us at privacy@signaly.com
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">9. Children's Privacy</h2>
            <p className="text-muted-foreground">
              The App is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* International Data Transfers */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">10. International Data Transfers</h2>
            <p className="text-muted-foreground">
              Your information may be transferred to and maintained on servers located outside of your country of residence. By using the App, you consent to the transfer of your information to countries that may have different data protection laws than your jurisdiction.
            </p>
          </section>

          {/* California Privacy Rights (CCPA) */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">11. California Privacy Rights (CCPA)</h2>
            <p className="text-muted-foreground mb-3">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Right to know what personal information we collect and how we use it</li>
              <li>Right to delete your personal information</li>
              <li>Right to opt-out of the sale of personal information (we do not sell your data)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise your CCPA rights, email us at privacy@signaly.com
            </p>
          </section>

          {/* European Privacy Rights (GDPR) */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">12. European Privacy Rights (GDPR)</h2>
            <p className="text-muted-foreground mb-3">If you are in the European Economic Area (EEA), you have rights under the General Data Protection Regulation (GDPR):</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Right to access, correct, and delete your data</li>
              <li>Right to data portability</li>
              <li>Right to restrict or object to processing</li>
              <li>Right to withdraw consent</li>
              <li>Right to lodge a complaint with your supervisory authority</li>
            </ul>
            <p className="text-muted-foreground mt-3 mb-2">Our legal basis for processing your data includes:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Consent (for marketing communications)</li>
              <li>Contract performance (to provide the App's services)</li>
              <li>Legitimate interests (to improve our services and prevent fraud)</li>
            </ul>
          </section>

          {/* Push Notifications */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">13. Push Notifications</h2>
            <p className="text-muted-foreground">
              We send push notifications for new trading signals, price alerts, and important account updates. You can manage notification preferences in your device settings or within the App.
            </p>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">14. Cookies and Tracking</h2>
            <p className="text-muted-foreground">
              The App uses local storage and analytics tools to improve user experience. We do not use browser cookies as this is a native mobile application. You can disable analytics tracking in your account settings.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">15. Changes to This Policy</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-app notification. Your continued use of the App after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-4">16. Contact Us</h2>
            <p className="text-muted-foreground mb-3">If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-muted-foreground"><strong>Email:</strong> support@signaly.me</p>
              <p className="text-muted-foreground"><strong>Support:</strong> support@signaly.me</p>
              <p className="text-muted-foreground"><strong>Address:</strong> 8 Devonshire Square London</p>
              <p className="text-muted-foreground mt-3"><strong>For GDPR-related inquiries:</strong> support@signaly.me</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <section className="border-t border-border pt-6">
            <p className="text-muted-foreground italic">
              By using Signaly, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
